import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { BugSeverity, ReleaseDetail, ReleaseKnownIssue } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { SEVERITY_LABELS, blocksRelease } from "./releaseVocabulary";

const SEVERITIES: BugSeverity[] = ["BLOCKER", "CRITICAL", "MAJOR", "MINOR", "TRIVIAL"];

/**
 * Recording something known to be broken, or accepting the risk of shipping with it (§30).
 *
 * <p>Two different acts in one drawer because they are two halves of one decision, and keeping
 * them together is what makes the second one visible. Raising an issue does not accept it: a
 * blocker sits on the release holding the critical-bugs gate until a named person says the risk
 * is acceptable and says why.
 */
export function KnownIssueDrawer({
  release,
  issue,
  mode,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  issue: ReleaseKnownIssue | null;
  mode: "edit" | "accept";
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState("");
  const [severity, setSeverity] = useState<BugSeverity>("MAJOR");
  const [impact, setImpact] = useState("");
  const [workaround, setWorkaround] = useState("");
  const [comment, setComment] = useState("");
  const [acceptance, setAcceptance] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setTitle(issue?.title ?? "");
    setSeverity(issue?.severity ?? "MAJOR");
    setImpact(issue?.impact ?? "");
    setWorkaround(issue?.workaround ?? "");
    setComment(issue?.comment ?? "");
    setAcceptance("");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, issue, mode]);

  const accepting = mode === "accept";
  const canSubmit = accepting ? acceptance.trim() !== "" : title.trim() !== "";

  const submit = () =>
    void run(
      () =>
        accepting && issue
          ? api.post(`/api/v1/releases/${release.id}/known-issues/${issue.id}/accept-risk`, {
              comment: acceptance.trim(),
              lockVersion: issue.lockVersion,
            })
          : issue
            ? api.patch(`/api/v1/releases/${release.id}/known-issues/${issue.id}`, {
                title: title.trim(),
                severity,
                workItemId: issue.workItemId,
                impact: impact.trim() === "" ? null : impact.trim(),
                workaround: workaround.trim() === "" ? null : workaround.trim(),
                comment: comment.trim() === "" ? null : comment.trim(),
                lockVersion: issue.lockVersion,
              })
            : api.post(`/api/v1/releases/${release.id}/known-issues`, {
                title: title.trim(),
                severity,
                workItemId: null,
                impact: impact.trim() === "" ? null : impact.trim(),
                workaround: workaround.trim() === "" ? null : workaround.trim(),
                comment: comment.trim() === "" ? null : comment.trim(),
                lockVersion: null,
              }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={accepting ? "Accept the risk" : issue ? "Edit known issue" : "Record a known issue"}
      subtitle={
        accepting
          ? issue?.title
          : "Something that is broken and is going out anyway, or might."
      }
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : accepting ? "Accept the risk" : issue ? "Save" : "Record"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        {accepting ? (
          <>
            <InlineAlert tone="warning">
              Your name goes on this. The issue stays on the release permanently, and the
              readiness gate will stop holding it back — this is the record that somebody decided
              to ship with a known {SEVERITY_LABELS[issue?.severity ?? "MAJOR"].toLowerCase()}{" "}
              problem.
            </InlineAlert>
            <Field
              label="Why is this acceptable?"
              required
              hint="What an incident review would need to read."
            >
              <Textarea
                rows={4}
                value={acceptance}
                onChange={(event) => setAcceptance(event.target.value)}
                placeholder="Gateway B is 2% of volume and the fix ships on Monday."
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="What is broken?" required>
              <Input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Card payments fail on gateway B"
              />
            </Field>
            <Field
              label="Severity"
              hint={
                blocksRelease(severity)
                  ? "This will hold the release until somebody accepts the risk."
                  : "Recorded on the release, but it will not hold it back."
              }
            >
              <Select
                value={severity}
                onChange={(event) => setSeverity(event.target.value as BugSeverity)}
              >
                {SEVERITIES.map((value) => (
                  <option key={value} value={value}>
                    {SEVERITY_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Impact" hint="Who is affected, and how badly.">
              <Textarea
                rows={3}
                value={impact}
                onChange={(event) => setImpact(event.target.value)}
              />
            </Field>
            <Field label="Workaround" hint="What support can tell a customer who hits this.">
              <Textarea
                rows={2}
                value={workaround}
                onChange={(event) => setWorkaround(event.target.value)}
              />
            </Field>
            <Field label="Notes">
              <Textarea
                rows={2}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
